import { useEffect, useState, useMemo } from "react";
// doc-design-vendors2: vendors workspace (with vendor-types)
import { PageFrame, DocMenu } from "@/components/procurement/DocKit";
import { List, LayoutGrid, Check, Eye, User, Phone, Mail, AlertCircle, AlertTriangle, CheckCircle2, XCircle, Search, RotateCcw, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Users2 } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { VendorContactDialog } from "@/components/procurement/VendorContactDialog";
import { confirmDuplicate, getErrorMessage, getFieldErrors } from "@/lib/errors";
import { partyLabel } from "@/lib/format";

const VENDOR_TYPES = [["PURCHASE", "Purchase Order", "Materials supplied against purchase orders"], ["WORK", "Work Order", "Contractors engaged through work orders"], ["EQUIPMENT", "Equipment", "Machinery and equipment hire"]];
const typeLabel = (k) => VENDOR_TYPES.find((t) => t[0] === k)?.[1] || k;
const typesOf = (v) => (Array.isArray(v.vendor_types) && v.vendor_types.length ? v.vendor_types : ["PURCHASE"]);

function TypeChips({ vendor }) {
  return (
    <div className="mt-1 flex flex-wrap gap-1" data-testid={`vendor-types-${vendor.code}`}>
      {typesOf(vendor).map((k) => <span key={k} className="rounded-full bg-[#910029]/10 px-2 py-0.5 text-[11px] font-medium text-[#910029]">{typeLabel(k)}</span>)}
    </div>
  );
}

function TypePicker({ value, onChange }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div className="grid gap-2 sm:grid-cols-3" data-testid="vendor-type-picker">
      {VENDOR_TYPES.map(([k, label, hint]) => {
        const on = value.includes(k);
        return (
          <button key={k} type="button" onClick={() => toggle(k)} aria-pressed={on} data-testid={`vendor-type-${k}`}
            className={`rounded-xl border p-3 text-left transition-colors ${on ? "border-[#910029] bg-[#910029]/5" : "border-border hover:bg-accent/40"}`}>
            <span className="flex items-center gap-2 text-sm font-semibold text-foreground"><span aria-hidden className={`grid h-4 w-4 shrink-0 place-items-center rounded border ${on ? "border-[#910029] bg-[#910029] text-white" : "border-border bg-background"}`}>{on && <Check className="h-3 w-3" />}</span>{label}</span>
            <span className="mt-1 block text-[11px] text-muted-foreground">{hint}</span>
          </button>
        );
      })}
    </div>
  );
}

// Branches a vendor serves. None picked = the vendor shows on every branch.
function useBranches() {
  const [branches, setBranches] = useState([]);
  useEffect(() => { api.get("/masters/branches").then(({ data }) => setBranches(data.filter((b) => b.is_active))).catch(() => {}); }, []);
  return branches;
}

function BranchChips({ vendor }) {
  const names = vendor.branch_names || [];
  return (
    <div className="mt-1 flex flex-wrap gap-1" data-testid={`vendor-branches-${vendor.code}`}>
      {names.length
        ? names.map((n) => <span key={n} className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{n}</span>)
        : <span className="text-[11px] text-muted-foreground">All branches</span>}
    </div>
  );
}

function BranchPicker({ branches, value, onChange }) {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div data-testid="vendor-branch-picker">
      <div className="flex flex-wrap gap-2">
        {branches.map((b) => {
          const on = value.includes(b.id);
          return (
            <button key={b.id} type="button" onClick={() => toggle(b.id)} aria-pressed={on} data-testid={`vendor-branch-${b.code}`}
              className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${on ? "border-[#910029] bg-[#910029]/5 text-foreground" : "border-border text-muted-foreground hover:bg-accent/40"}`}>
              <span aria-hidden className={`grid h-4 w-4 shrink-0 place-items-center rounded border ${on ? "border-[#910029] bg-[#910029] text-white" : "border-border bg-background"}`}>{on && <Check className="h-3 w-3" />}</span>{b.name}
            </button>
          );
        })}
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">{value.length ? "Shown only under the branches picked." : "None picked: shown under every branch."}</p>
    </div>
  );
}

function VendorBranchesDialog({ vendor, onClose, onSaved }) {
  const branches = useBranches();
  const [value, setValue] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (vendor) setValue(vendor.branch_ids || []); }, [vendor]);
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/procurement/vendors/${vendor.id}`, { branch_ids: value }); toast.success("Vendor branches updated"); onClose(); onSaved?.(); }
    catch (err) { toast.error(getErrorMessage(err)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!vendor} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="vendor-branches-dialog">
        <DialogHeader><DialogTitle>{vendor?.name}: branches</DialogTitle></DialogHeader>
        <BranchPicker branches={branches} value={value} onChange={setValue} />
        <DialogFooter><Button onClick={save} disabled={saving} data-testid="vendor-branches-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VendorTypesDialog({ vendor, onClose, onSaved }) {
  const [value, setValue] = useState(["PURCHASE"]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (vendor) setValue(typesOf(vendor)); }, [vendor]);
  const save = async () => {
    if (value.length === 0) { toast.error("Pick at least one category"); return; }
    setSaving(true);
    try { await api.patch(`/procurement/vendors/${vendor.id}`, { vendor_types: value }); toast.success("Vendor categories updated"); onClose(); onSaved?.(); }
    catch (err) { toast.error(getErrorMessage(err)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!vendor} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl" data-testid="vendor-types-dialog">
        <DialogHeader><DialogTitle>{vendor?.name} — categories</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">A vendor can be in more than one category. Purchase Order vendors appear when raising POs, Work Order vendors when raising work orders.</p>
        <TypePicker value={value} onChange={setValue} />
        <DialogFooter><Button onClick={save} disabled={saving} data-testid="vendor-types-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TH = "h-11 text-xs font-semibold text-muted-foreground";
const iconBtn = "grid h-9 w-9 place-items-center rounded-lg border border-border bg-background text-foreground hover:bg-accent";
const PAGE_SIZES = [5, 10, 25, 50];
const NO_FILTERS = { search: "", vendor: "", status: "", type: "" };
const missingContact = (v) => v.is_active && (!(v.phone || "").trim() || !(v.email || "").trim());

function VTile({ icon: Icon, label, value, active, onClick, tone, testId }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active} data-testid={testId}
      className={`flex items-center gap-4 rounded-2xl border bg-card px-5 py-4 text-left shadow-sm transition-colors hover:bg-accent/40 ${active ? "border-[#910029] ring-1 ring-[#910029]/30" : "border-border"}`}>
      <span className={`grid h-12 w-12 shrink-0 place-items-center rounded-xl ${tone || "bg-[#910029]/10 text-[#910029]"}`}><Icon className="h-5 w-5" /></span>
      <span className="min-w-0"><span className="block text-sm text-muted-foreground">{label}</span><span className="block text-2xl font-bold tabular-nums text-foreground">{value}</span></span>
    </button>
  );
}

function SortTh({ label, k, sort, onSort, className = "" }) {
  const on = sort.key === k;
  return (
    <TableHead className={`${TH} ${className}`}>
      <button type="button" onClick={() => onSort(k)} className="inline-flex items-center gap-1 hover:text-foreground" data-testid={`vendor-sort-${k}`}>
        {label}<ArrowUpDown className={`h-3 w-3 ${on ? "text-[#910029]" : "opacity-50"}`} />
      </button>
    </TableHead>
  );
}

function ViewVendorDialog({ vendor, onClose }) {
  const row = (l, v) => <div className="flex justify-between gap-6 text-sm"><span className="text-muted-foreground">{l}</span><span className="text-right text-foreground">{v || "—"}</span></div>;
  return (
    <Dialog open={!!vendor} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="vendor-view-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-3">{vendor?.name} {vendor && <StatusPill value={vendor.is_active ? "ACTIVE" : "INACTIVE"} />}</DialogTitle></DialogHeader>
        {vendor && (
          <div className="space-y-1.5 rounded-xl border border-border bg-secondary/40 p-4">
            {row("Categories", typesOf(vendor).map(typeLabel).join(", "))}{row("Branches", (vendor.branch_names || []).join(", ") || "All branches")}{row("Code", vendor.code)}{row("GSTIN", vendor.gstin)}{row("PAN", vendor.pan)}{row("State", vendor.state)}
            {row("Contact person", vendor.contact_person)}{row("Phone", vendor.phone)}{row("Email", vendor.email)}
            {row("Default retention", `${Number(vendor.default_retention_pct || 0).toFixed(2)}%`)}{row("Default TDS", `${Number(vendor.default_tds_pct || 0).toFixed(2)}%`)}{row("TDS section", vendor.tds_section)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Vendors() {
  const { can } = usePermissions();
  const canAdd = can("VENDORS", "create");
  const canEditVendor = can("VENDORS", "edit");
  const canEditContact = can("VENDORS", "edit");   // PATCH /procurement/vendors/{id}/contact is checked against the same "Vendors: Edit" row
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tile, setTile] = useState("ALL");
  const [f, setF] = useState(NO_FILTERS);
  const [sort, setSort] = useState({ key: "code", dir: "asc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selected, setSelected] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [contactFor, setContactFor] = useState(null);
  const [termsFor, setTermsFor] = useState(null);
  const [typesFor, setTypesFor] = useState(null);
  const [branchesFor, setBranchesFor] = useState(null);
  const [mode, setModeState] = useState(() => { try { return localStorage.getItem("vendors-view") === "card" ? "card" : "list"; } catch { return "list"; } });
  const setMode = (m) => { setModeState(m); try { localStorage.setItem("vendors-view", m); } catch { /* ignore */ } };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/procurement/vendors");
      setVendors(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (vendor) => {
    try {
      await api.patch(`/procurement/vendors/${vendor.id}`, { is_active: !vendor.is_active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  const onSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const missing = vendors.filter(missingContact);
  const sortVal = (v, k) => ({ code: v.code, name: v.name, gst: v.gstin || "", contact: v.contact_person || "", terms: Number(v.default_retention_pct || 0), status: v.is_active ? 1 : 0 }[k]);

  const shown = useMemo(() => {
    const t = f.search.trim().toLowerCase();
    const list = vendors.filter((v) => {
      if (tile === "ACTIVE" && !v.is_active) return false;
      if (tile === "INACTIVE" && v.is_active) return false;
      if (tile === "MISSING" && !missingContact(v)) return false;
      if (f.vendor && v.id !== f.vendor) return false;
      if (f.type && !typesOf(v).includes(f.type)) return false;
      if (f.status === "ACTIVE" && !v.is_active) return false;
      if (f.status === "INACTIVE" && v.is_active) return false;
      if (t && !`${v.code} ${v.name} ${v.gstin || ""} ${v.pan || ""} ${v.phone || ""} ${v.email || ""} ${v.contact_person || ""}`.toLowerCase().includes(t)) return false;
      return true;
    });
    const m = sort.dir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => { const x = sortVal(a, sort.key), y = sortVal(b, sort.key); return (x > y ? 1 : x < y ? -1 : 0) * m; });
  }, [vendors, tile, f, sort]);
  useEffect(() => { setPage(1); }, [tile, f, sort, pageSize]);

  const pages = Math.max(1, Math.ceil(shown.length / pageSize));
  const cur = Math.min(page, pages);
  const rows = shown.slice((cur - 1) * pageSize, cur * pageSize);
  const from = shown.length ? (cur - 1) * pageSize + 1 : 0;
  const allOnPage = rows.length > 0 && rows.every((v) => selected.includes(v.id));
  const toggleAll = (val) => setSelected(val ? [...new Set([...selected, ...rows.map((v) => v.id)])] : selected.filter((id) => !rows.some((v) => v.id === id)));
  const dirty = JSON.stringify(f) !== JSON.stringify(NO_FILTERS) || tile !== "ALL";
  const contactBlock = (v) => (
    <div className="text-xs leading-5">
      <p className="flex items-center gap-2 text-foreground"><User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />{v.contact_person || "—"}</p>
      {v.phone ? <p className="flex items-center gap-2 text-muted-foreground"><Phone className="h-3.5 w-3.5 shrink-0" />{v.phone}</p> : warn("No phone")}
      {v.email ? <p className="flex items-center gap-2 text-muted-foreground"><Mail className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{v.email}</span></p> : warn("No email")}
    </div>
  );
  const rowActions = (v) => (
                <div className="flex items-center justify-end gap-1.5">
                  <button type="button" title="View" onClick={() => setViewing(v)} className={iconBtn} data-testid={`vendor-view-${v.code}`}><Eye className="h-4 w-4" /></button>
                  {canEditContact && <button type="button" title="Edit contact" onClick={() => setContactFor(v)} className={iconBtn} data-testid={`vendor-contact-button-${v.code}`}><Pencil className="h-4 w-4" /></button>}
                  <DocMenu testId={`vendor-actions-${v.code}`} items={[
                    { key: "view", label: "View details", icon: Eye, onSelect: () => setViewing(v) },
                    canEditContact && { key: "contact", label: "Edit contact", icon: Phone, onSelect: () => setContactFor(v) },
                    canEditVendor && { key: "types", label: "Edit categories", icon: Pencil, onSelect: () => setTypesFor(v), testId: `vendor-types-button-${v.code}` },
                    canEditVendor && { key: "branches", label: "Edit branches", icon: Pencil, onSelect: () => setBranchesFor(v), testId: `vendor-branches-button-${v.code}` },
    canEditVendor && { key: "terms", label: "Edit retention / TDS / PAN", icon: Pencil, onSelect: () => setTermsFor(v), testId: `vendor-terms-button-${v.code}` },
                    { key: "act", label: v.is_active ? "Deactivate" : "Activate", icon: v.is_active ? XCircle : CheckCircle2, onSelect: () => toggleActive(v), danger: v.is_active },
                  ]} />
                </div>
  );
  const warn = (txt) => <p className="flex items-center gap-2 text-amber-600 dark:text-amber-400"><AlertCircle className="h-3.5 w-3.5 shrink-0" />{txt}</p>;

  return (
    <PageFrame testId="vendors-page" crumbs={[{ label: "Procurement" }, { label: "Vendors" }]} title="Vendors"
      description="Supplier master data used across indents, RFQs and purchase orders."
      actions={canAdd ? <CreateVendorDialog onCreated={load} /> : null}>
      {missing.length > 0 && tile !== "MISSING" && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200" role="status" data-testid="vendors-missing-contact-banner">
          <span className="inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4 shrink-0" /><span><strong>{missing.length}</strong> active vendor{missing.length > 1 ? "s have" : " has"} no phone or no email, so RFQ links cannot be sent to {missing.length > 1 ? "them" : "it"}.</span></span>
          <Button size="sm" variant="outline" className="h-9 rounded-lg" onClick={() => setTile("MISSING")} data-testid="vendors-show-missing-contact">Show and fix <ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="vendor-tiles">
        <VTile icon={Users2} label="All Vendors" value={vendors.length} active={tile === "ALL"} onClick={() => setTile("ALL")} testId="vendor-tab-ALL" />
        <VTile icon={CheckCircle2} label="Active" value={vendors.filter((v) => v.is_active).length} active={tile === "ACTIVE"} onClick={() => setTile("ACTIVE")} testId="vendor-tab-ACTIVE" />
        <VTile icon={XCircle} label="Inactive" value={vendors.filter((v) => !v.is_active).length} active={tile === "INACTIVE"} onClick={() => setTile("INACTIVE")} testId="vendor-tab-INACTIVE" />
        <VTile icon={AlertCircle} label="Missing Contact" value={missing.length} active={tile === "MISSING"} onClick={() => setTile("MISSING")} testId="vendor-tab-MISSING" />
      </div>

      <div className="flex flex-wrap items-center gap-2.5 rounded-2xl border border-border bg-card p-3.5 shadow-sm" data-testid="vendor-filter-bar">
        <div className="relative min-w-[220px] flex-[2]">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="vendor-search" value={f.search} onChange={(e) => set({ search: e.target.value })} placeholder="Search by code, name, GSTIN, vendor, phone or email…"
            className="h-11 w-full rounded-lg border border-border bg-background pl-10 pr-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-[#910029] focus:ring-2 focus:ring-[#910029]/20" />
        </div>
        <Select value={f.vendor || "ALL"} onValueChange={(v) => set({ vendor: v === "ALL" ? "" : v })}>
          <SelectTrigger className="h-11 w-48 text-sm [&>span]:truncate" data-testid="vendor-filter-vendor"><SelectValue placeholder="All Vendors" /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">All Vendors</SelectItem>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{partyLabel(v)}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.type || "ALL"} onValueChange={(v) => set({ type: v === "ALL" ? "" : v })}>
          <SelectTrigger className="h-11 w-44 text-sm" data-testid="vendor-filter-type"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">All Categories</SelectItem>{VENDOR_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.status || "ALL"} onValueChange={(v) => set({ status: v === "ALL" ? "" : v })}>
          <SelectTrigger className="h-11 w-40 text-sm" data-testid="vendor-filter-status"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">All Status</SelectItem><SelectItem value="ACTIVE">Active</SelectItem><SelectItem value="INACTIVE">Inactive</SelectItem></SelectContent>
        </Select>
        <button type="button" onClick={() => { setF(NO_FILTERS); setTile("ALL"); }} disabled={!dirty} data-testid="vendor-filter-reset" className="inline-flex h-11 items-center gap-1.5 px-3 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"><RotateCcw className="h-4 w-4" /> Clear</button>
      </div>

      <div className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-5">
        <div className="mb-3 flex justify-end">
          <div className="inline-flex overflow-hidden rounded-lg border border-border" role="group" data-testid="vendor-view-toggle">
            {[["list", "List", List], ["card", "Card", LayoutGrid]].map(([k, label, Icon]) => (
              <button key={k} type="button" onClick={() => setMode(k)} aria-pressed={mode === k} data-testid={`vendor-view-${k}`}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium ${mode === k ? "bg-[#910029] text-white" : "bg-card text-muted-foreground hover:bg-accent"}`}><Icon className="h-4 w-4" />{label}</button>
            ))}
          </div>
        </div>
        {!loading && shown.length === 0 ? (
          <div className="rounded-xl border border-border"><EmptyState title={vendors.length === 0 ? "No vendors yet" : "No vendors match"} description={vendors.length === 0 ? undefined : "Try changing or clearing the filters."} testId="vendors-empty-state" /></div>
        ) : mode === "card" ? (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="vendor-cards">
            {rows.map((v) => (
              <div key={v.id} className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm" data-testid={`vendor-card-${v.code}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0"><p className="text-xs font-semibold text-muted-foreground">{v.code}</p><p className="text-base font-bold leading-tight text-foreground">{v.name}</p><TypeChips vendor={v} /></div>
                  <StatusPill value={v.is_active ? "ACTIVE" : "INACTIVE"} />
                </div>
                <div className="space-y-1 text-xs">
                  <p className="font-mono text-muted-foreground">{v.gstin || "No GSTIN"}</p>
                  {v.pan ? <p className="font-mono text-muted-foreground">PAN {v.pan}</p> : <p className="text-amber-600 dark:text-amber-400">PAN missing</p>}
                </div>
                {contactBlock(v)}
                <div className="mt-auto flex items-center justify-between gap-3 border-t border-border pt-3">
                  <p className="text-xs tabular-nums text-muted-foreground"><span className="block text-[11px]">Retention / TDS</span>{Number(v.default_retention_pct || 0).toFixed(2)}% / {Number(v.default_tds_pct || 0).toFixed(2)}%</p>
                  {rowActions(v)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border">
            <Table>
              <TableHeader><TableRow className="bg-secondary/60 hover:bg-secondary/60">
                <TableHead className="h-11 w-12 pl-5"><Checkbox checked={allOnPage} onCheckedChange={toggleAll} aria-label="Select all" data-testid="vendor-select-all" /></TableHead>
                <SortTh label="Code" k="code" sort={sort} onSort={onSort} /><SortTh label="Name" k="name" sort={sort} onSort={onSort} /><SortTh label="GSTIN / PAN" k="gst" sort={sort} onSort={onSort} />
                <SortTh label="Contact / Phone / Email" k="contact" sort={sort} onSort={onSort} /><SortTh label="Retention / TDS" k="terms" sort={sort} onSort={onSort} />
                <SortTh label="Status" k="status" sort={sort} onSort={onSort} /><TableHead className={`${TH} pr-5 text-right`}>Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((v) => (
                  <TableRow key={v.id} className="h-[84px]" data-testid={`vendor-row-${v.code}`} data-state={selected.includes(v.id) ? "selected" : undefined}>
                    <TableCell className="pl-5"><Checkbox checked={selected.includes(v.id)} onCheckedChange={(val) => setSelected(val ? [...selected, v.id] : selected.filter((id) => id !== v.id))} aria-label={`Select ${v.code}`} /></TableCell>
                    <TableCell className="text-sm font-semibold text-foreground">{v.code}</TableCell>
                    <TableCell className="text-sm text-foreground">{v.name}<TypeChips vendor={v} /><BranchChips vendor={v} /></TableCell>
                    <TableCell className="text-xs">
                      <p className="font-mono text-muted-foreground">{v.gstin || "—"}</p>
                      {v.pan ? <p className="font-mono text-muted-foreground">PAN {v.pan}</p> : <p className="text-amber-600 dark:text-amber-400">PAN missing</p>}
                    </TableCell>
                    <TableCell>{contactBlock(v)}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">{Number(v.default_retention_pct || 0).toFixed(2)}% / {Number(v.default_tds_pct || 0).toFixed(2)}%</TableCell>
                    <TableCell><StatusPill value={v.is_active ? "ACTIVE" : "INACTIVE"} testId={`vendor-status-${v.code}`} /></TableCell>
                    <TableCell className="pr-5">
                      {rowActions(v)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3" data-testid="vendor-pager">
          <p className="text-sm text-muted-foreground">Showing {from} - {from ? from + rows.length - 1 : 0} of {shown.length} vendor{shown.length === 1 ? "" : "s"}{selected.length > 0 ? ` · ${selected.length} selected` : ""}</p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Items per page</span>
            <Select value={String(pageSize)} onValueChange={(val) => setPageSize(Number(val))}>
              <SelectTrigger className="h-9 w-20 text-sm" data-testid="vendor-page-size"><SelectValue /></SelectTrigger>
              <SelectContent>{PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
            </Select>
            <button type="button" disabled={cur <= 1} onClick={() => setPage(cur - 1)} aria-label="Previous page" className={`${iconBtn} disabled:opacity-40`}><ChevronLeft className="h-4 w-4" /></button>
            {Array.from({ length: pages }, (_, i) => i + 1).slice(Math.max(0, cur - 3), Math.max(0, cur - 3) + 5).map((n) => (
              <button key={n} type="button" onClick={() => setPage(n)} aria-current={n === cur ? "page" : undefined}
                className={`grid h-9 w-9 place-items-center rounded-lg border text-sm font-semibold ${n === cur ? "border-[#910029] bg-[#910029] text-white" : "border-border text-muted-foreground hover:bg-accent"}`}>{n}</button>
            ))}
            <button type="button" disabled={cur >= pages} onClick={() => setPage(cur + 1)} aria-label="Next page" className={`${iconBtn} disabled:opacity-40`}><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      <ViewVendorDialog vendor={viewing} onClose={() => setViewing(null)} />
      <VendorTypesDialog vendor={typesFor} onClose={() => setTypesFor(null)} onSaved={load} />
      <VendorBranchesDialog vendor={branchesFor} onClose={() => setBranchesFor(null)} onSaved={load} />
      {contactFor && <VendorContactDialog vendor={contactFor} open onOpenChange={(o) => { if (!o) setContactFor(null); }} onSaved={load} />}
      {termsFor && <VendorTermsDialog vendor={termsFor} open onOpenChange={(o) => { if (!o) setTermsFor(null); }} onSaved={load} />}
    </PageFrame>
  );
}

function CreateVendorDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [suggested, setSuggested] = useState("");
  useEffect(() => {
    if (open) api.get("/codes/next", { params: { kind: "vendor" } }).then(({ data }) => setSuggested(data.code)).catch(() => setSuggested(""));
  }, [open]);
  const [form, setForm] = useState({ code: "", name: "", gstin: "", state: "", contact_person: "", phone: "", email: "", default_retention_pct: "0", default_tds_pct: "0", pan: "", tds_section: "194C", vendor_types: ["PURCHASE"], branch_ids: [] });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const branches = useBranches();
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      if (form.vendor_types.length === 0) { toast.error("Pick at least one category"); setSaving(false); return; }
      const create = (allow) => api.post("/procurement/vendors", { ...form, code: form.code.trim() || null, pan: form.pan || null }, allow ? { params: { allow_duplicate_name: true } } : undefined);
      let res;
      try { res = await create(false); } catch (err) { if (await confirmDuplicate(err)) res = await create(true); else throw err; }
      const { data: created } = res;
      toast.success(`Vendor ${created.code} created`);
      setForm({ code: "", name: "", gstin: "", state: "", contact_person: "", phone: "", email: "", default_retention_pct: "0", default_tds_pct: "0", pan: "", tds_section: "194C", vendor_types: ["PURCHASE"], branch_ids: [] });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-vendor-button"><Plus className="h-4 w-4" /> Add New Vendor</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Vendor</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-vendor-form">
          <div><Label>Category</Label><div className="mt-1.5"><TypePicker value={form.vendor_types} onChange={(t) => setForm({ ...form, vendor_types: t })} /></div><FieldError error={errors.vendor_types} /></div>
          {branches.length > 1 && <div><Label>Branches</Label><div className="mt-1.5"><BranchPicker branches={branches} value={form.branch_ids} onChange={(ids) => setForm({ ...form, branch_ids: ids })} /></div><FieldError error={errors.branch_ids} /></div>}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code <span className="font-normal text-muted-foreground">(auto)</span></Label><Input data-testid="vendor-code-input" value={form.code} onChange={set("code")} placeholder={suggested || "Auto"} className="mt-1.5" /><p className="mt-0.5 text-[10px] text-muted-foreground">Leave blank to use {suggested || "the next number"}</p><FieldError error={errors.code} /></div>
            <div><Label>Name</Label><Input required data-testid="vendor-name-input" value={form.name} onChange={set("name")} className="mt-1.5" /><FieldError error={errors.name} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>GSTIN</Label><Input data-testid="vendor-gstin-input" value={form.gstin} onChange={set("gstin")} className="mt-1.5" /></div>
            <div><Label>State</Label><Input data-testid="vendor-state-input" value={form.state} onChange={set("state")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Contact person</Label><Input data-testid="vendor-contact-input" value={form.contact_person} onChange={set("contact_person")} className="mt-1.5" /></div>
            <div><Label>Phone / WhatsApp</Label><Input data-testid="vendor-phone-input" inputMode="tel" value={form.phone} onChange={set("phone")} className="mt-1.5" /><FieldError error={errors.phone} /></div>
          </div>
          <div><Label>Email</Label><Input type="email" data-testid="vendor-email-input" value={form.email} onChange={set("email")} placeholder="sales@vendor.com" className="mt-1.5" /><FieldError error={errors.email} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Default retention %</Label><Input type="number" step="0.01" min="0" max="100" data-testid="vendor-retention-input" value={form.default_retention_pct} onChange={set("default_retention_pct")} className="mt-1.5" /></div>
            <div><Label>Default TDS %</Label><Input type="number" step="0.01" min="0" max="100" data-testid="vendor-tds-input" value={form.default_tds_pct} onChange={set("default_tds_pct")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>PAN</Label><Input data-testid="vendor-pan-input" value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} maxLength={10} placeholder="ABCDE1234F" className="mt-1.5" /></div>
            <div>
              <Label>TDS section</Label>
              <Select value={form.tds_section} onValueChange={(v) => setForm({ ...form, tds_section: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="194C">194C — Contractor / subcontractor</SelectItem>
                <SelectItem value="194J">194J — Professional / technical fees</SelectItem>
                <SelectItem value="194I">194I — Rent (machinery / land)</SelectItem>
                <SelectItem value="194H">194H — Commission</SelectItem>
                <SelectItem value="194Q">194Q — Purchase of goods</SelectItem>
              </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-vendor-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function VendorTermsDialog({ vendor, onSaved, open: controlledOpen, onOpenChange }) {
  const [innerOpen, setInnerOpen] = useState(false);
  const controlled = controlledOpen !== undefined;
  const open = controlled ? controlledOpen : innerOpen;
  const setOpen = controlled ? onOpenChange : setInnerOpen;
  const [form, setForm] = useState({ default_retention_pct: "0", default_tds_pct: "0", pan: "", tds_section: "194C" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ default_retention_pct: String(vendor.default_retention_pct), default_tds_pct: String(vendor.default_tds_pct), pan: vendor.pan || "", tds_section: vendor.tds_section || "194C" });
  }, [open, vendor]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/procurement/vendors/${vendor.id}`, { ...form, pan: form.pan || null });
      toast.success("Vendor retention / TDS / PAN updated");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!controlled && <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className="h-7 px-2" title="Edit retention / TDS" data-testid={`vendor-terms-button-${vendor.code}`}><Pencil className="h-3.5 w-3.5" /></Button>
      </DialogTrigger>}
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>{vendor.name} — billing terms</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Default retention %</Label><Input type="number" step="0.01" min="0" max="100" value={form.default_retention_pct} onChange={(e) => setForm({ ...form, default_retention_pct: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Default TDS %</Label><Input type="number" step="0.01" min="0" max="100" value={form.default_tds_pct} onChange={(e) => setForm({ ...form, default_tds_pct: e.target.value })} className="mt-1.5" /></div>
            <div><Label>PAN</Label><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} maxLength={10} placeholder="ABCDE1234F" className="mt-1.5" /></div>
            <div>
              <Label>TDS section</Label>
              <Select value={form.tds_section} onValueChange={(v) => setForm({ ...form, tds_section: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="194C">194C — Contractor / subcontractor</SelectItem>
                <SelectItem value="194J">194J — Professional / technical fees</SelectItem>
                <SelectItem value="194I">194I — Rent (machinery / land)</SelectItem>
                <SelectItem value="194H">194H — Commission</SelectItem>
                <SelectItem value="194Q">194Q — Purchase of goods</SelectItem>
              </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">Applies to new service bills. Bills already created keep their own %.</p>
          <DialogFooter>
            <Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}